import React, { useContext, useState, useEffect } from 'react';
import { SelectedCompanyContext } from '../contexts/SelectedCompanyContext';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { ModernCard, ModernButton, AnimatedBackground } from './ui/modern';
import { getFallbackTemplates, useCompanyTaskTemplates } from '../hooks/queries/useTaskTemplates';

const STEPS = [
  { id: 1, title: 'Internal Systems', icon: '⚙️', description: 'Select internal systems to configure' },
  { id: 2, title: 'Company Tasks', icon: '📋', description: 'Choose company-wide tasks and dates' },
  { id: 3, title: 'Server Info', icon: '🖥️', description: 'Configure server information' },
  { id: 4, title: 'Branch Services', icon: '🏢', description: 'Setup services per branch' },
];

// Modern Step Indicator Component
const StepIndicator = ({ steps, currentStep }) => (
  <div className="flex items-center justify-center mb-8">
    {steps.map((step, index) => {
      const isCompleted = currentStep > step.id;
      const isCurrent = currentStep === step.id;
      const isLast = index === steps.length - 1;
      
      return (
        <div key={step.id} className="flex items-center">
          {/* Step Circle */}
          <div className="flex flex-col items-center">
            <div 
              className={`
                w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold
                transition-all duration-300 border-2
                ${isCurrent 
                  ? 'bg-gradient-to-br from-blue-500 to-violet-500 border-blue-400 text-white shadow-lg shadow-blue-500/30 scale-110' 
                  : isCompleted 
                    ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                    : 'bg-white/5 border-white/20 text-slate-500'
                }
              `}
            >
              {isCompleted ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                step.icon
              )}
            </div>
            <span className={`mt-2 text-xs font-medium ${isCurrent ? 'text-white' : 'text-slate-500'}`}>
              {step.title}
            </span>
          </div>
          
          {/* Connector Line */}
          {!isLast && (
            <div 
              className={`w-16 md:w-24 h-0.5 mx-2 transition-all duration-300 ${
                isCompleted ? 'bg-emerald-500' : 'bg-white/10'
              }`}
            />
          )}
        </div>
      );
    })}
  </div>
);

// Modern Checkbox Card Component
const CheckboxCard = ({ item, checked, onChange, showDescription = true }) => (
  <label 
    className={`
      relative flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200
      ${checked 
        ? 'bg-gradient-to-r from-blue-600/20 to-violet-600/20 border-2 border-blue-500/50 shadow-lg shadow-blue-500/10' 
        : 'bg-white/5 border-2 border-white/10 hover:border-white/20 hover:bg-white/10'
      }
    `}
  >
    <div className={`
      w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200
      ${checked 
        ? 'bg-gradient-to-br from-blue-500 to-violet-500' 
        : 'bg-white/10 border border-white/20'
      }
    `}>
      {checked && (
        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
    <input 
      type="checkbox" 
      checked={checked} 
      onChange={onChange}
      className="sr-only" 
    />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-xl">{item.icon}</span>
        <span className="text-white font-medium">{item.label}</span>
      </div>
      {showDescription && item.description && (
        <p className="text-slate-400 text-sm mt-1">{item.description}</p>
      )}
    </div>
    {checked && (
      <span className="absolute top-2 right-2 text-xs text-blue-400 font-medium">Selected</span>
    )}
  </label>
);

// Modern Radio Card Component
const RadioCard = ({ label, icon, checked, onChange, name }) => (
  <label 
    className={`
      flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200
      ${checked 
        ? 'bg-gradient-to-r from-emerald-600/20 to-teal-600/20 border-2 border-emerald-500/50' 
        : 'bg-white/5 border-2 border-white/10 hover:border-white/20'
      }
    `}
  >
    <div className={`
      w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all duration-200
      ${checked ? 'border-emerald-500' : 'border-white/30'}
    `}>
      {checked && (
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
      )}
    </div>
    <input 
      type="radio" 
      name={name}
      checked={checked} 
      onChange={onChange}
      className="sr-only" 
    />
    <span className="text-xl">{icon}</span>
    <span className="text-white font-medium">{label}</span>
  </label>
);

// Modern Date Input Component
const ModernDateInput = ({ label, value, onChange }) => (
  <div className="flex flex-col gap-2">
    <label className="text-sm font-medium text-slate-400">{label}</label>
    <input
      type="date"
      value={value}
      onChange={onChange}
      className="
        w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10
        text-white placeholder-slate-500
        focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50
        transition-all duration-200
      "
    />
  </div>
);

const OnboardingWizard = () => {
  const { setSelected } = useContext(SelectedCompanyContext);
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { data: templatesData } = useCompanyTaskTemplates(companyId);

  const [step, setStep] = useState(1);
  const [systems, setSystems] = useState([]);
  const [companyTasks, setCompanyTasks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchIdx, setBranchIdx] = useState(0);
  const [branchServiceMap, setBranchServiceMap] = useState({});
  const [onboardingStart, setOnboardingStart] = useState('');
  const [onboardingEnd, setOnboardingEnd] = useState('');
  const [hasServers, setHasServers] = useState(false);
  const [serverLocation, setServerLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState('');

  const templates = (Array.isArray(templatesData) && templatesData.length > 0)
    ? templatesData
    : getFallbackTemplates();

  const internalOptions = templates
    .filter((t) => t.group === 'internalSystems' && t.isActive !== false)
    .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
    .map((t) => ({ id: t.title, label: t.title, icon: t.icon || '⚙️', description: '' }));

  const companyTaskOptions = templates
    .filter((t) => t.group === 'companyWide' && t.isActive !== false)
    .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
    .map((t) => ({ id: t.title, label: t.title, icon: t.icon || '📋', description: '' }));

  const branchServiceOptions = templates
    .filter((t) => t.group === 'branchServices' && t.isActive !== false)
    .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
    .map((t) => ({ id: t.title, label: t.title, icon: t.icon || '🏢', description: '' }));

  const next = () => setStep(step + 1);
  const prev = () => setStep(step - 1);

  const toggle = (listSetter, list) => (item) => {
    listSetter(list.includes(item) ? list.filter(i => i !== item) : [...list, item]);
  };

  // Load company name
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await api.get(`/companies/${companyId}`);
        setCompanyName(res.data.name);
      } catch (err) {
        console.error('Error fetching company', err);
      }
    };
    fetchCompany();
  }, [companyId]);

  // Load branches when reaching step 4
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await api.get(`/companies/${companyId}/branches`);
        setBranches(res.data);
        if (res.data.length) setBranchIdx(0);
      } catch (err) {
        console.error('Error fetching branches', err);
      }
    };
    if (step === 4 && branches.length === 0) fetchBranches();
  }, [step, companyId, branches.length]);

  const currentBranch = branches[branchIdx] || null;

  const toggleBranchService = (service) => {
    if (!currentBranch) return;
    setBranchServiceMap(prev => {
      const list = prev[currentBranch.id] || [];
      const updated = list.includes(service) ? list.filter(s => s !== service) : [...list, service];
      return { ...prev, [currentBranch.id]: updated };
    });
  };

  const handleFinish = async () => {
    if (systems.length === 0) {
      setError('Please select at least one internal system');
      return;
    }
    setSaving(true);
    setError('');
    
    try {
      const existingTasks = await api.get(`/tasks/company/${companyId}`).then(r => r.data);

      const datePayload = {
        startDate: onboardingStart || null,
        completionDate: onboardingEnd || null,
      };

      const taskExists = (type, branchId = null) => {
        return existingTasks.some(t => t.type === type && ((branchId || null) === (t.branchId || null)));
      };

      // Create internal system tasks
      for (const type of systems) {
        if (!taskExists(type)) {
          const created = await api.post(`/tasks/company/${companyId}`, { type, status: 'Not Started', ...datePayload });
          existingTasks.push(created.data);
        }
      }

      // Create company-wide tasks
      for (const type of companyTasks) {
        if (!taskExists(type)) {
          const created = await api.post(`/tasks/company/${companyId}`, { type, status: 'Not Started', ...datePayload });
          existingTasks.push(created.data);
        }
      }

      // Create branch-specific service tasks and initialize onboarding
      for (const [bIdStr, services] of Object.entries(branchServiceMap)) {
        const bId = parseInt(bIdStr);
        if (services.length === 0) continue;
        
        // Create CompanyTask for each selected branch service (for UI display only, not counted in progress)
        for (const service of services) {
          if (!taskExists(service, bId)) {
            const created = await api.post(`/tasks/company/${companyId}`, { 
              type: service, 
              status: 'Not Started', 
              branchId: bId,
              ...datePayload 
            });
            existingTasks.push(created.data);
          }
        }
        
        // Initialize branch onboarding placeholders (users/devices)
        const branchDetail = branches.find(b => b.id === bId) || await api.get(`/branches/${bId}`).then(r => r.data);
        const totalDevices = (branchDetail.numberOfNetworkDevices || 0) + (branchDetail.numberOfDevices || 0);
        await api.post(`/branches/${bId}/onboarding/init`, { usersCount: branchDetail.numberOfUsers || 0, devicesCount: totalDevices });
      }

      // Update company server info
      const companyPayload = { hasServers };
      if (hasServers) {
        companyPayload.serverLocation = serverLocation || null;
      } else {
        companyPayload.serverLocation = null;
      }
      await api.put(`/companies/${companyId}`, companyPayload);

      // Refresh company
      const refreshed = await api.get(`/companies/${companyId}`);
      setSelected(refreshed.data);
      navigate(`/onboarding/${companyId}`);
    } catch (e) {
      console.error(e);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <AnimatedBackground />
      
      <div className="relative z-10 p-6 md:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            🚀 Onboarding Wizard
          </h1>
          {companyName && (
            <p className="text-slate-400 text-lg">
              Setting up <span className="text-blue-400 font-semibold">{companyName}</span>
            </p>
          )}
        </div>

        {/* Step Indicator */}
        <StepIndicator steps={STEPS} currentStep={step} />

        {/* Main Content Card */}
        <ModernCard className="mb-6">
          {/* Step Title */}
          <div className="mb-6 pb-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{STEPS[step - 1]?.icon}</span>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {STEPS[step - 1]?.title}
                </h2>
                <p className="text-slate-400 text-sm">
                  {STEPS[step - 1]?.description}
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
              ⚠️ {error}
            </div>
          )}

          {/* Step 1: Internal Systems */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {internalOptions.map(sys => (
                <CheckboxCard
                  key={sys.id}
                  item={sys}
                  checked={systems.includes(sys.id)}
                  onChange={() => toggle(setSystems, systems)(sys.id)}
                />
              ))}
            </div>
          )}

          {/* Step 2: Company Tasks */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Date Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <ModernDateInput
                  label="📅 Onboarding Start Date"
                  value={onboardingStart}
                  onChange={e => setOnboardingStart(e.target.value)}
                />
                <ModernDateInput
                  label="🏁 Onboarding End Date"
                  value={onboardingEnd}
                  onChange={e => setOnboardingEnd(e.target.value)}
                />
              </div>

              <h3 className="text-lg font-semibold text-white mb-4">
                Select Tasks to Include:
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {companyTaskOptions.map(task => (
                  <CheckboxCard
                    key={task.id}
                    item={task}
                    checked={companyTasks.includes(task.id)}
                    onChange={() => toggle(setCompanyTasks, companyTasks)(task.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Server Information */}
          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                Does the client have servers?
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md">
                <RadioCard
                  name="hasServers"
                  label="Yes"
                  icon="✅"
                  checked={hasServers}
                  onChange={() => setHasServers(true)}
                />
                <RadioCard
                  name="hasServers"
                  label="No"
                  icon="❌"
                  checked={!hasServers}
                  onChange={() => { setHasServers(false); setServerLocation(''); }}
                />
              </div>

              {hasServers && (
                <div className="mt-6 p-6 rounded-xl bg-white/5 border border-white/10">
                  <label className="block text-sm font-medium text-slate-400 mb-3">
                    🏢 Server Location
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {['Head Office', 'Cloud Based', 'Per Branch'].map(loc => (
                      <label
                        key={loc}
                        className={`
                          flex items-center justify-center gap-2 p-4 rounded-xl cursor-pointer transition-all duration-200
                          ${serverLocation === loc
                            ? 'bg-gradient-to-r from-blue-600/20 to-violet-600/20 border-2 border-blue-500/50'
                            : 'bg-white/5 border-2 border-white/10 hover:border-white/20'
                          }
                        `}
                      >
                        <input
                          type="radio"
                          name="serverLocation"
                          value={loc}
                          checked={serverLocation === loc}
                          onChange={e => setServerLocation(e.target.value)}
                          className="sr-only"
                        />
                        <span className="text-xl">
                          {loc === 'Head Office' ? '🏛️' : loc === 'Cloud Based' ? '☁️' : '🏢'}
                        </span>
                        <span className="text-white font-medium">{loc}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Branch Services */}
          {step === 4 && (
            <div className="space-y-6">
              {currentBranch ? (
                <>
                  {/* Branch Navigator */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-violet-600/10 to-blue-600/10 border border-violet-500/20">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center text-2xl">
                        🏢
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">{currentBranch.name}</h3>
                        <p className="text-sm text-slate-400">
                          Branch {branchIdx + 1} of {branches.length}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-slate-400">
                        {currentBranch.numberOfUsers || 0} Users • {(currentBranch.numberOfDevices || 0) + (currentBranch.numberOfNetworkDevices || 0)} Devices
                      </div>
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-white">
                    Services to Setup:
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {branchServiceOptions.map(service => (
                      <CheckboxCard
                        key={service.id}
                        item={service}
                        checked={(branchServiceMap[currentBranch.id] || []).includes(service.id)}
                        onChange={() => toggleBranchService(service.id)}
                        showDescription={false}
                      />
                    ))}
                  </div>

                  {/* Branch Progress Indicator */}
                  {branches.length > 1 && (
                    <div className="flex items-center gap-2 pt-4">
                      {branches.map((_, idx) => (
                        <div
                          key={idx}
                          className={`
                            h-2 flex-1 rounded-full transition-all duration-300
                            ${idx < branchIdx ? 'bg-emerald-500' : idx === branchIdx ? 'bg-blue-500' : 'bg-white/10'}
                          `}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <span className="text-6xl mb-4 block">🏢</span>
                  <h3 className="text-xl font-bold text-white mb-2">No Branches Found</h3>
                  <p className="text-slate-400">
                    Add branches to this company before configuring branch services.
                  </p>
                </div>
              )}
            </div>
          )}
        </ModernCard>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <div>
            {step > 1 && (
              <ModernButton variant="secondary" onClick={prev}>
                ← Back
              </ModernButton>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            <ModernButton 
              variant="glass" 
              onClick={() => navigate(`/company/${companyId}`)}
            >
              Cancel
            </ModernButton>
            
            {step < 4 ? (
              <ModernButton variant="primary" onClick={next}>
                Next Step →
              </ModernButton>
            ) : (
              branchIdx < branches.length - 1 ? (
                <ModernButton variant="primary" onClick={() => setBranchIdx(branchIdx + 1)}>
                  Next Branch →
                </ModernButton>
              ) : (
                <ModernButton 
                  variant="success" 
                  onClick={handleFinish} 
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    '✓ Complete Setup'
                  )}
                </ModernButton>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
